import React, { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import logo from "../../images/logo2.png";

export default function ResetPassword() {
  const { token } = useParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/reset-password/${token}`, {
        newPassword,
      });
      toast.success("Password reset successfully!");
      navigate("/auth");
    } catch (error) {
      toast.error(
        "Error resetting password: " +
          (error.response?.data?.message || "Unexpected error occurred."),
      );
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.2,
      },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute w-96 h-96 bg-yellow-200/10 rounded-full -top-48 -left-48" />
        <div className="absolute w-96 h-96 bg-yellow-200/10 rounded-full -bottom-48 -right-48" />
      </div>

      <motion.div
        className="w-full max-w-md z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          variants={childVariants}
          className="flex mb-4 p-1 bg-white backdrop-blur-sm justify-center items-center rounded-2xl shadow-sm border border-white/20"
        >
          <img src={logo} alt="logo" className="h-12 sm:h-16" />
        </motion.div>
        <motion.div
          className="bg-white/95 backdrop-blur-lg shadow-xl rounded-2xl p-8 border border-white/20"
          variants={childVariants}
        >
          <div className="text-center mb-8">
            <motion.h2
              className="text-3xl font-bold text-yellow-500"
              variants={childVariants}
            >
              Reset Your Password
            </motion.h2>
            <motion.p className="text-gray-600 mt-2" variants={childVariants}>
              Enter your new password
            </motion.p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <motion.div variants={childVariants}>
              <label
                htmlFor="newPassword"
                className="block text-left text-sm font-medium text-gray-700 mb-1"
              >
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400 z-10" />
                </div>
                <motion.input
                  id="newPassword"
                  type="password"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white text-gray-900 placeholder-gray-500"
                  required
                  whileFocus={{ scale: 1.02 }}
                />
              </div>
            </motion.div>

            <motion.div variants={childVariants}>
              <label
                htmlFor="confirmPassword"
                className="block text-left text-sm font-medium text-gray-700 mb-1"
              >
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400 z-10" />
                </div>
                <motion.input
                  id="confirmPassword"
                  type="password"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white text-gray-900 placeholder-gray-500"
                  required
                  whileFocus={{ scale: 1.02 }}
                />
              </div>
            </motion.div>

            <motion.button
              id="sub_btn"
              type="submit"
              disabled={loading}
              className="w-full bg-yellow-500 text-white py-3 rounded-lg hover:bg-yellow-600 transition-all duration-300 flex items-center justify-center font-medium shadow-md hover:shadow-lg disabled:opacity-75 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              variants={childVariants}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </motion.button>
          </form>

          <motion.footer
            className="mt-6 text-center text-sm space-y-2"
            variants={childVariants}
          >
            <p>
              <Link
                to="/auth"
                className="text-yellow-500 hover:text-yellow-600 font-medium transition-colors duration-200"
              >
                Back to Login
              </Link>
            </p>
          </motion.footer>
        </motion.div>
      </motion.div>
    </div>
  );
}

// import React, { useState } from 'react';
// import { useParams, useNavigate,Link } from 'react-router-dom';
// import axios from 'axios';
// import './Authentication.css';
// import {API_BASE_URL} from '../../config';
// export default function ResetPassword() {
//     const { token } = useParams(); // Get the token from the URL
//     const [newPassword, setNewPassword] = useState('');
//     const [confirmPassword, setConfirmPassword] = useState('');
//     const navigate = useNavigate();

//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         if (newPassword !== confirmPassword) {
//             return alert("Passwords do not match");
//         }

//         try {
//             await axios.post(`${API_BASE_URL}/api/auth/reset-password/${token}`, { newPassword });
//             alert("Password reset successfully!");
//             navigate("/auth"); // Redirect to login after resetting the password
//         } catch (error) {
//             alert("Error resetting password: " + error.response.data.message);
//         }
//     };

//     return (
//         <div className="text-center m-5-auto">
//             <h2>Reset your password</h2>
//             <form onSubmit={handleSubmit}>
//                 <p>
//                     <label>New Password</label><br/>
//                     <input
//                         type="password"
//                         name="newPassword"
//                         required
//                         value={newPassword}
//                         onChange={(e) => setNewPassword(e.target.value)}
//                     />
//                 </p>
//                 <p>
//                     <label>Confirm Password</label><br/>
//                     <input
//                         type="password"
//                         name="confirmPassword"
//                         required
//                         value={confirmPassword}
//                         onChange={(e) => setConfirmPassword(e.target.value)}
//                     />
//                 </p>
//                 <p>
//                     <button id="sub_btn" type="submit">Reset Password</button>
//                 </p>
//             </form>
//             <footer>
//                 <p><Link to="/auth">Back to Login</Link>.</p>
//             </footer>
//         </div>
//     );
// }
